import React from 'react';
import { useCpsDialog } from '../../../common/useCpsDialog';
import { useConfigurationKeysManagementPermissions } from '../hooks/useConfigurationKeysManagementPermissions';
import { NumericTextBox, NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import {
  useGlobalizationContext,
  useTranslation,
  useGetGlobalFiltersLimit,
  useUpsertUiAdminConfigEntry,
  useGetIndexes
} from '@cs-cms/state-management';
import {
  CpsIndex,
  GLOBAL_FILTERS_LIMIT_MAX,
  GLOBAL_FILTERS_LIMIT_MIN,
  GLOBAL_FILTERS_LIMIT_KEY_PREFIX,
  CpsUiAdminConfigType
} from '@cs-cms/types';

export const GlobalFiltersLimitConfig: React.FC = () => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showDialog, DialogComponent } = useCpsDialog();
  const [localLimit, setLocalLimit] = React.useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState<CpsIndex | null>(null);
  const { data: currentLimit, isLoading, error } = useGetGlobalFiltersLimit(selectedIndex?.id);
  const upsertConfigMutation = useUpsertUiAdminConfigEntry();
  const { data: indexes } = useGetIndexes();

  const { canEditGlobalFiltersLimit, isLoadingPermissions } = useConfigurationKeysManagementPermissions(
    selectedIndex?.id
  );

  React.useEffect(() => {
    if (indexes && indexes.length > 0 && !selectedIndex) {
      setSelectedIndex(indexes[0]);
    }
  }, [indexes, selectedIndex]);

  const indexOptions =
    indexes?.map(index => ({
      text: index.displayName || index.name,
      value: index.id,
      dataItem: index
    })) || [];

  const displayLimit = localLimit ?? currentLimit ?? GLOBAL_FILTERS_LIMIT_MAX;
  const hasUnsavedChanges = localLimit !== null && localLimit !== currentLimit;
  const isSaving = upsertConfigMutation.isPending;

  const handleIndexChange = (event: any) => {
    const selected = event.target.value;
    if (selected?.dataItem) {
      setSelectedIndex(selected.dataItem);
      setLocalLimit(null);
    }
  };

  const handleLimitChange = (event: NumericTextBoxChangeEvent) => {
    const newValue = event.target.value;

    if (newValue !== null) {
      const clampedValue = Math.min(Math.max(newValue, GLOBAL_FILTERS_LIMIT_MIN), GLOBAL_FILTERS_LIMIT_MAX);
      setLocalLimit(clampedValue);
    } else {
      setLocalLimit(newValue);
    }
  };

  const handleSave = async () => {
    if (!selectedIndex || localLimit === null) {
      return;
    }

    showDialog({
      title: translate('custom.common.saveChangesTitle'),
      message: translate('custom.settings.adminCenter.configurations.saveChangesMessage'),
      confirmButtonLabel: translate('custom.common.save'),
      discardButtonLabel: translate('custom.common.cancel'),
      onConfirm: async confirmed => {
        if (confirmed) {
          const configKey = `${GLOBAL_FILTERS_LIMIT_KEY_PREFIX}${selectedIndex.id}` as CpsUiAdminConfigType;

          await upsertConfigMutation.mutateAsync({
            key: configKey,
            body: String(localLimit)
          });

          setLocalLimit(null);
        }
      }
    });
  };

  const selectedOption =
    !selectedIndex || !indexOptions.length ? null : indexOptions.find(option => option.value === selectedIndex.id);

  return (
    <>
      <div className="configurationsCard">
        <div className="configurationsCardHeader">
          <h3 className="configurationsCardTitle">
            {translate('custom.settings.adminCenter.configurations.globalFiltersLimit.title')}
          </h3>
          <p className="configurationsCardDescription">
            {translate('custom.settings.adminCenter.configurations.globalFiltersLimit.description')}
          </p>
        </div>

        <div className="configurationsCardContent">
          <div className="configurationsFormRow">
            <div className="configurationsFormField">
              <label className="configurationsLabel">
                {translate('custom.settings.adminCenter.configurations.index.label')}
              </label>
              <DropDownList
                data={indexOptions}
                textField="text"
                dataItemKey="value"
                value={selectedOption}
                onChange={handleIndexChange}
              />
            </div>

            <div className="configurationsFormField">
              <label className="configurationsLabel">
                {translate('custom.settings.adminCenter.configurations.limit.label')}
              </label>
              <NumericTextBox
                value={displayLimit}
                onChange={handleLimitChange}
                min={GLOBAL_FILTERS_LIMIT_MIN}
                max={GLOBAL_FILTERS_LIMIT_MAX}
                placeholder={translate('custom.settings.adminCenter.configurations.limit.placeholder')}
                disabled={isLoading || isSaving || !canEditGlobalFiltersLimit}
              />
            </div>
          </div>

          <div className="configurationsCurrentSetting">
            {isLoading || isLoadingPermissions ? (
              <p className="configurationsCurrentText">
                {translate('custom.settings.adminCenter.configurations.loadingLimit')}
              </p>
            ) : error ? (
              <p className="configurationsErrorText">
                {translate('custom.settings.adminCenter.configurations.errorLoadingLimit')}
              </p>
            ) : !canEditGlobalFiltersLimit ? (
              <p className="configurationsErrorText">
                {translate('custom.settings.adminCenter.configurations.noWritePermission')}
              </p>
            ) : (
              <p className="configurationsCurrentText">
                {translate('custom.settings.adminCenter.configurations.currentSetting')}{' '}
                {selectedIndex?.displayName || selectedIndex?.name}: {currentLimit}{' '}
                {translate('custom.settings.adminCenter.configurations.filtersMaximum')}
              </p>
            )}
            {hasUnsavedChanges && (
              <p className="configurationsUnsavedNotice">
                {translate('custom.settings.adminCenter.configurations.unsavedChanges')}
              </p>
            )}
          </div>
        </div>

        <div className="configurationsCardActions">
          <Button
            type="button"
            themeColor="primary"
            onClick={handleSave}
            className="configurationsSaveButton"
            disabled={!selectedIndex || !hasUnsavedChanges || isLoading || isSaving || !canEditGlobalFiltersLimit}
          >
            {isSaving
              ? translate('custom.settings.adminCenter.configurations.saving')
              : translate('custom.settings.adminCenter.configurations.save')}
          </Button>
        </div>
      </div>
      {DialogComponent}
    </>
  );
};
